import EditIcon from '@mui/icons-material/Edit';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { ClientOverview } from '../types';
import ClientOverviewEditor from './ClientOverviewEditor';
import ContactItem from './ContactItem';

interface ClientOverviewSectionProps {
  clientOverview: ClientOverview;
  onUpdate: (updatedOverview: ClientOverview) => void;
}

const ClientOverviewSection: React.FC<ClientOverviewSectionProps> = ({
  clientOverview,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useUser();

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleEditorClose = () => {
    setIsEditing(false);
  };

  const handleEditorSave = (updatedOverview: ClientOverview) => {
    onUpdate(updatedOverview);
    setIsEditing(false);
  };

  const getCurrentPhaseColor = (phase: string) => {
    switch (phase) {
      case 'in production': return '#22c55e';
      case 'testing': return '#facc15';
      case 'development': return '#3b82f6';
      case 'planning': return '#d1d5db';
      case 'discontinued': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  const getCurrentPhaseText = (phase: string) => {
    switch (phase) {
      case 'in production': return 'In Production';
      case 'testing': return 'Testing';
      case 'development': return 'Development';
      case 'planning': return 'Planning';
      case 'discontinued': return 'Discontinued';
      default: return phase;
    }
  };

  return (
    <>
      <section id="client-overview" className="mb-16">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-satoshi font-bold text-3xl m-0 flex items-center gap-3">
            <PersonOutlineIcon className="text-3xl" />
            Client Overview
          </h2>
          {user?.canEdit ? <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick();
              }}
              style={{
                background: 'transparent',
                color: '#3b82f6',
                border: '1px solid #bfdbfe',
                borderRadius: 8,
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#eff6ff';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              <EditIcon style={{ fontSize: 14 }} />
              Edit
            </button>
          </div> : null}
        </div>

        <table className="w-full border-collapse bg-white rounded-xl overflow-hidden">
          <tbody className="font-satoshi">
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 w-48 text-gray-900">Client</td>
              <td className="p-4 text-lg">
                <div className="flex items-center justify-between">
                  <span>{clientOverview.client.name}</span>
                  {clientOverview.client.country === 'US' && (
                    <span className="text-sm">🇺🇸 US Based</span>
                  )}
                </div>
              </td>
            </tr>
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 text-gray-900">Provider</td>
              <td className="p-4 text-lg">
                <a href={clientOverview.provider.url} className="text-blue-600 hover:text-blue-700 no-underline">
                  {clientOverview.provider.name}
                </a>
              </td>
            </tr>
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 text-gray-900">OpenAI Tools in use</td>
              <td className="p-4 text-lg">{clientOverview.openAITools}</td>
            </tr>
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 text-gray-900">Use Case</td>
              <td className="p-4 text-lg">{clientOverview.useCase}</td>
            </tr>
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 text-gray-900">Start Date</td>
              <td className="p-4 text-lg">{clientOverview.startDate}</td>
            </tr>
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 text-gray-900">Current Phase</td>
              <td className="p-4 text-lg">
                {clientOverview.currentPhase && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        marginRight: 8,
                        background: getCurrentPhaseColor(clientOverview.currentPhase)
                      }} />
                      <span>{getCurrentPhaseText(clientOverview.currentPhase)}</span>
                    </div>
                    {/* <PlayCircleFilledWhiteOutlinedIcon className="text-blue-600" /> */}
                  </div>
                )}
              </td>
            </tr>
            <tr className="border-b border-[#cccccc]">
              <td className="font-bold p-4 text-gray-900">Project Size & Scale</td>
              <td className="p-4 text-lg">
                <div className="space-y-1">
                  {clientOverview.projectScale.projectSize && clientOverview.projectScale.projectSize.split(",").map((item, index) => (
                    <div key={index}>• {item.trim()}</div>
                  ))}
                </div>
              </td>
            </tr>
            <tr>
              <td className="font-bold p-4 text-gray-900">Contact us</td>
              <td className="p-4 text-lg">
                <div className="flex flex-wrap gap-2">
                  {clientOverview.contacts.map((contact) => (
                    <ContactItem
                      key={contact.id}
                      contact={contact}
                    />
                  ))}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <ClientOverviewEditor
        isOpen={isEditing}
        onClose={handleEditorClose}
        clientOverview={clientOverview}
        onSave={handleEditorSave}
      />
    </>
  );
};

export default ClientOverviewSection;